/**
 * Video URL Utilities
 * 
 * Detects and parses YouTube/Vimeo URLs for personal video uploads.
 * Reuses logic from coaching system for consistency.
 */

export type VideoProvider = 'youtube' | 'vimeo';

export interface ParsedVideoUrl {
  provider: VideoProvider;
  providerId: string;
  url: string;
  thumbnailUrl: string;
}

/**
 * Parse video URL to extract provider and provider_id
 * Supports YouTube and Vimeo URLs
 */
export function parseVideoUrl(url: string): { provider: VideoProvider; providerId: string } | null {
  // YouTube patterns
  const youtubeRegexes = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const regex of youtubeRegexes) {
    const match = url.match(regex);
    if (match && match[1]) {
      return { provider: 'youtube', providerId: match[1] };
    }
  }

  // Vimeo patterns
  const vimeoRegex = /vimeo\.com\/(\d+)/;
  const vimeoMatch = url.match(vimeoRegex);
  if (vimeoMatch && vimeoMatch[1]) {
    return { provider: 'vimeo', providerId: vimeoMatch[1] };
  }

  return null;
}

/**
 * Generate thumbnail URL for video provider
 */
export function generateThumbnailUrl(provider: VideoProvider, providerId: string): string {
  if (provider === 'youtube') {
    return `https://img.youtube.com/vi/${providerId}/hqdefault.jpg`;
  } else if (provider === 'vimeo') {
    // Vimeo thumbnails via vumbnail.com service
    return `https://vumbnail.com/${providerId}.jpg`;
  }
  return '';
}

/**
 * Check if a string is a valid YouTube or Vimeo URL
 */
export function isVideoUrl(input: string): boolean {
  return parseVideoUrl(input) !== null;
}

/**
 * Parse and validate a video URL, returning full details
 */
export function parseAndValidateVideoUrl(url: string): ParsedVideoUrl | null {
  const parsed = parseVideoUrl(url);
  if (!parsed) {
    return null;
  }

  return {
    provider: parsed.provider,
    providerId: parsed.providerId,
    url: url, // Return original URL
    thumbnailUrl: generateThumbnailUrl(parsed.provider, parsed.providerId),
  };
}
